import { FaYoutube, FaInstagram, FaTiktok, FaXTwitter, FaTwitch, FaFacebook, FaCircleQuestion } from 'react-icons/fa6';
import platformService from '../services/platformService';

// Maps iconName strings (stored in the DB) to actual React icon components.
// Backend returns e.g. "FaYoutube"; frontend looks it up here.
// Add entries here when integrating new platforms with custom icons.
export const ICON_MAP = {
  FaYoutube,
  FaTiktok,
  FaInstagram,
  FaXTwitter,
  FaTwitch,
  FaFacebook,
  FaCircleQuestion, // fallback for unknown platforms
};

// Static fallback used before the API loads (or if the API call fails).
// Includes all 6 platforms so the UI is never empty.
export const PLATFORMS = [
  {
    value: 'YOUTUBE',
    label: 'YouTube',
    icon: FaYoutube,
    color: 'text-red-600',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-600',
    brandColor: '#FF0000',
  },
  {
    value: 'TIKTOK',
    label: 'TikTok',
    icon: FaTiktok,
    color: 'text-black dark:text-white',
    bgColor: 'bg-gray-50 dark:bg-gray-800',
    borderColor: 'border-gray-900 dark:border-white',
    brandColor: '#000000',
  },
  {
    value: 'INSTAGRAM',
    label: 'Instagram',
    icon: FaInstagram,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50 dark:bg-pink-900/20',
    borderColor: 'border-pink-600',
    brandColor: '#E4405F',
  },
  {
    value: 'FACEBOOK',
    label: 'Facebook',
    icon: FaFacebook,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-600',
    brandColor: '#1877F2',
  },
  {
    value: 'TWITCH',
    label: 'Twitch',
    icon: FaTwitch,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    borderColor: 'border-purple-600',
    brandColor: '#9146FF',
  },
  {
    value: 'TWITTER',
    label: 'X (Twitter)',
    icon: FaXTwitter,
    color: 'text-black dark:text-white',
    bgColor: 'bg-gray-50 dark:bg-gray-800',
    borderColor: 'border-gray-900 dark:border-white',
    brandColor: '#000000',
  },
];

/**
 * Determines if a platform is "coming soon" based on API data.
 * A platform is coming soon if it either doesn't support OAuth or
 * doesn't have a code integration available.
 *
 * @param {Object} platformDef - Platform definition from the API
 * @returns {boolean}
 */
export const isPlatformComingSoon = (platformDef) => {
  return !platformDef?.oauthSupported || !platformDef?.integrationAvailable;
};

/**
 * Fetches platform definitions from the API and maps them to the PLATFORMS
 * shape consumed by SettingsPage, OnboardingWizard, and other components.
 *
 * Each API entry has:
 *   platformKey, displayName, iconName, textColorClass, bgColorClass, borderColorClass,
 *   integrationAvailable, publishSupported, supportedContentTypes, rateLimitMaxCalls, etc.
 *
 * This function maps them to:
 *   value, label, icon (React component via ICON_MAP), color, bgColor, borderColor,
 *   integrationAvailable, publishSupported, comingSoon (derived)
 *
 * Falls back to the static PLATFORMS array if the API call fails.
 */
export const loadPlatformDefinitions = async () => {
  try {
    const definitions = await platformService.getPlatformDefinitions();
    const activeKeys = new Set(definitions.map((d) => d.platformKey));

    // Map API-returned (active) platforms
    const activePlatforms = definitions.map((def) => ({
      value: def.platformKey,
      label: def.displayName,
      icon: ICON_MAP[def.iconName] ?? FaCircleQuestion,
      color: def.textColorClass,
      bgColor: def.bgColorClass,
      borderColor: def.borderColorClass,
      description: def.description,
      oauthSupported: def.oauthSupported,
      category: def.category,
      brandColor: def.brandColor,
      integrationAvailable: def.integrationAvailable,
      publishSupported: def.publishSupported,
      supportedContentTypes: def.supportedContentTypes,
      rateLimitMaxCalls: def.rateLimitMaxCalls,
      rateLimitWindowSeconds: def.rateLimitWindowSeconds,
      maxTitleLength: def.maxTitleLength,
      maxDescriptionLength: def.maxDescriptionLength,
      comingSoon: !def.oauthSupported || !def.integrationAvailable,
    }));

    // Append static platforms that are NOT active (admin disabled) as "Coming Soon"
    const disabledPlatforms = PLATFORMS
      .filter((p) => !activeKeys.has(p.value))
      .map((p) => ({ ...p, comingSoon: true }));

    return [...activePlatforms, ...disabledPlatforms];
  } catch {
    // API unavailable — return static fallback so the UI stays functional
    return PLATFORMS;
  }
};

export const getPlatformData = (platformValue) => {
  return PLATFORMS.find((p) => p.value === platformValue);
};

export const getPlatformIcon = (platformValue) => {
  const platform = getPlatformData(platformValue);
  return platform?.icon;
};

export const getPlatformColor = (platformValue) => {
  const platform = getPlatformData(platformValue);
  return platform?.color;
};
